import { useQuery } from "@tanstack/react-query";
import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import { getAccessToken } from "../../api/getAccessToken";
import {
  mapRawFuturesLeagues,
  type League,
  type RawFuturesLeagueItem,
} from "../../components/Configurations/SportsbookFM/Futures/futuresTransforms";
import {
  ENTITY_TYPES,
  type RawSport,
} from "../../components/Configurations/SportsbookFM/EventMarkets/eventMarketsTypes";
import { mapRawSportsToSports } from "../../components/Configurations/SportsbookFM/EventMarkets/eventMarketsTransforms";

export type FutureBonusSport = { id: string; name: string };
export type FutureBonusLeague = League;
export type FutureBonusTrait = {
  trait_id: string;
  display_name: string;
  market_settlement_date: string | null;
  eligible_market_types: string[];
};

type TraitsApiBody = {
  data?: {
    league_id?: string;
    traits?: FutureBonusTrait[];
  };
};

function adminApi() {
  return createPrivateAdminPanelInstance(getAccessToken());
}

async function fetchSports(): Promise<FutureBonusSport[]> {
  const res = await adminApi().post(API_ROUTES.GET_EVENT_MARKETS_SPORTS, {
    entity_type: ENTITY_TYPES.PREMATCH,
  });
  const sports = (res?.data?.sports || []) as RawSport[];
  return mapRawSportsToSports(sports).map((sport) => ({
    id: String(sport.id),
    name: sport.name,
  }));
}

async function fetchLeagues(sportId: string): Promise<FutureBonusLeague[]> {
  const res = await adminApi().get(
    API_ROUTES.GET_FUTURES_LEAGUES.replace("{sport_id}", sportId),
  );
  const raw = res?.data?.data;
  const items = (
    Array.isArray(raw) ? raw : raw?.items || []
  ) as RawFuturesLeagueItem[];
  // Only real sportsbook league ids are selectable here: this id is persisted
  // to the campaign, so the positional `league_${idx}` fallback must not escape.
  return mapRawFuturesLeagues(items).filter(
    (league) => !/^league_\d+$/.test(league.id),
  );
}

async function fetchTraits(
  sportId: string,
  leagueId: string,
): Promise<FutureBonusTrait[]> {
  const res = await adminApi().get(
    API_ROUTES.GET_FUTURES_TRAITS.replace("{sport_id}", sportId).replace(
      "{league_id}",
      leagueId,
    ),
  );
  const body = res?.data as TraitsApiBody | undefined;
  return body?.data?.traits ?? [];
}

export const FUTURE_BONUS_SPORTS_QUERY_KEY = ["FUTURE_BONUS_SPORTS"] as const;

export function useFutureBonusSports(enabled = true) {
  return useQuery<FutureBonusSport[]>({
    queryKey: FUTURE_BONUS_SPORTS_QUERY_KEY,
    queryFn: fetchSports,
    enabled,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
}

export function useFutureBonusLeagues(sportId?: string) {
  return useQuery<FutureBonusLeague[]>({
    queryKey: ["FUTURE_BONUS_LEAGUES", sportId],
    queryFn: () => fetchLeagues(sportId as string),
    enabled: Boolean(sportId),
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
}

export function useFutureBonusTraits(sportId?: string, leagueId?: string) {
  return useQuery<FutureBonusTrait[]>({
    queryKey: ["FUTURE_BONUS_TRAITS", sportId, leagueId],
    queryFn: () => fetchTraits(sportId as string, leagueId as string),
    enabled: Boolean(sportId && leagueId),
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
}
